import { useCallback, useMemo } from 'react'

import type {
  CampaignBannerHeaderContentBlock,
  CompactCampaignBannerHeaderContentBlock,
  StandardRecipeItem,
} from '@library/api-menu-experience'
import type { RecipeUUID } from '@library/menu-service'

import { useBasket } from 'routes/Menu/domains/basket'

type MerchandisingBannerBasketContentBlock =
  | CampaignBannerHeaderContentBlock
  | CompactCampaignBannerHeaderContentBlock

type MerchandisingBannerBasketItem =
  | CampaignBannerHeaderContentBlock['items'][number]
  | CompactCampaignBannerHeaderContentBlock['items'][number]

export const useMerchandisingBannerBasket = (
  contentBlock: MerchandisingBannerBasketContentBlock,
) => {
  const {
    addRecipeByUuid,
    canAddRecipes,
    getNumPortionsForRecipeId,
    getQuantitiesForRecipeId,
    reachedLimit,
    removeRecipeByUuid,
  } = useBasket()

  const recipeById = useMemo(() => {
    const items = contentBlock.items as MerchandisingBannerBasketItem[]

    return Object.fromEntries(
      items
        .filter((item): item is StandardRecipeItem => item.type === 'standard-recipe')
        .map((recipe) => [recipe.id, recipe]),
    ) as Partial<Record<RecipeUUID, StandardRecipeItem>>
  }, [contentBlock.items])

  const getRecipeCardState = useCallback(
    (recipeId: RecipeUUID) => {
      const recipe = recipeById[recipeId]
      const quantity = getQuantitiesForRecipeId(recipeId)
      const isInBasket = quantity > 0

      return {
        isInBasket,
        isOutOfStock: recipe ? !recipe.isAvailable && !isInBasket : false,
        quantity,
        numPortions: getNumPortionsForRecipeId(recipeId),
      }
    },
    [getNumPortionsForRecipeId, getQuantitiesForRecipeId, recipeById],
  )

  const onAddClick = useCallback(
    (recipeId: RecipeUUID) => {
      const recipe = recipeById[recipeId]

      if (!recipe?.isAvailable) {
        return
      }

      addRecipeByUuid(recipeId, 'view_recipe_list_screen', {
        ...(recipe.urn !== undefined && { recipeUrn: recipe.urn }),
        position: 'collection_merchandising_header',
        source: 'merch_banner',
        merch_header_type: contentBlock.type,
        promotionId: recipe.analytics?.promotionId,
      })
    },
    [addRecipeByUuid, contentBlock.type, recipeById],
  )

  const onRemoveClick = useCallback(
    (recipeId: RecipeUUID) => {
      removeRecipeByUuid(recipeId, 'grid', 'collection_merchandising_header')
    },
    [removeRecipeByUuid],
  )

  return {
    canAddRecipes,
    getRecipeCardState,
    onAddClick,
    onRemoveClick,
    reachedLimit,
  }
}
